import TicketsBought from "./components/TicketsBought";
import Withdraw from "./components/Withdraw";
import LoadingDashboard from "./components/LoadingDashboard";
import { Ticket } from "../../../../models/Ticket";
import { USDC } from "../../../../models/UsdcValue";
import Headline from "../../../miscellaneous/Headline";
import Coupons from "./components/Coupons";
import { Address } from "viem";
import { ButtonTheme } from "../../../miscellaneous/ButtonTheme";

export interface DashboardProps {
  /**
   * The date of the draw.
   */
  drawDate?: Date;
  /**
   * The function to call when the user wants to withdraw the prize.
   */
  onWithdraw: () => void;
  /**
   * The function to call when the user wants to withdraw the donations.
   */
  onWithdrawDonations: () => void;
  /**
   * The function to call when the user wants to withdraw the royalties.
   */
  onWithdrawRoyalties: () => void;
  /**
   * Whether the prize is being withdrawn or not.
   */
  withdrawing: boolean;
  /**
   * Whether the donations are being withdrawn or not.
   */
  withdrawingDonations: boolean;
  /**
   * Whether the royalties are being withdrawn or not.
   */
  withdrawingRoyalties: boolean;
  /**
   * The address of the user.
   */
  address?: Address;
  /**
   * The tickets bought by the user, for the present draw.
   */
  ticketsBought: Ticket[];
  /**
   * Whether a wallet is connected or not.
   */
  isConnected: boolean;
  /**
   * Whether the wallet is connecting or not.
   */
  isConnecting: boolean;
  /**
   * The function to call when the user wants to connect the wallet.
   */
  connectWallet: () => void;
  /**
   * The function to call when the user wants to see more tickets.
   */
  onSeeMoreTickets: () => void;
  /**
   * The total withdrawable prize.
   */
  withdrawablePrize?: USDC;
  /**
   * The total withdrawable donations.
   */
  withdrawableDonations?: USDC;
  /**
   * The total withdrawable royalties.
   */
  withdrawableRoyalties?: USDC;
  /**
   * Ticket buy limit.
   */
  ticketLimitPerPurchase: number;
  /**
   * Whether the user has been a charity before or not.
   */
  hasBeenCharity: boolean;
  /**
   * Whether the user is an owner or not.
   */
  isOwner: boolean;
  /**
   * The points of the user.
   */
  points: number;
}

export default function Dashboard(props: DashboardProps) {
  return (
    <div className="flex flex-col justify-center">
      <div className="w-[50%] self-center mb-4 pt-[48px]">
        <Headline value={"DASHBOARD"}></Headline>
      </div>
      {/**
       * If the user is connected, show the prize and the tickets bought.
       * if not, show the loading dashboard component.
       */}
      {props.isConnected ? (
        <div className="flex lg:flex-row flex-col items-center justify-center self-center px-4 ~space-y-6/12 lg:space-y-0 space-x-0 lg:~space-x-6/12">
          <Withdraw
            value={props.withdrawablePrize}
            onWithdraw={props.onWithdraw}
            withdrawing={props.withdrawing}
            address={props.address}
            text={"PRIZES TO COLLECT"}
            tooltipText={
              "These are the funds you've been awarded, that you haven't yet withdrawn to your wallet."
            }
            theme={ButtonTheme.Mango}
          ></Withdraw>
          {props.hasBeenCharity && (
            <Withdraw
              value={props.withdrawableDonations}
              onWithdraw={props.onWithdrawDonations}
              withdrawing={props.withdrawingDonations}
              address={props.address}
              text={"DONATIONS TO COLLECT"}
              tooltipText={
                "These are the funds that have been donated to your cause, not yet withdrawn to your wallet."
              }
              theme={ButtonTheme.Bermuda}
            ></Withdraw>
          )}
          {props.isOwner && (
            <Withdraw
              value={props.withdrawableRoyalties}
              onWithdraw={props.onWithdrawRoyalties}
              withdrawing={props.withdrawingRoyalties}
              address={props.address}
              text={"ROYALTIES TO COLLECT"}
              tooltipText={
                "These are the royalties, not yet withdrawn to your wallet."
              }
              theme={ButtonTheme.Mango}
            ></Withdraw>
          )}
          <TicketsBought
            drawDate={props.drawDate}
            ticketsBought={props.ticketsBought}
            onSeeMore={props.onSeeMoreTickets}
          ></TicketsBought>
          <Coupons
            ticketLimitPerPurchase={props.ticketLimitPerPurchase}
            points={props.points}
            address={props.address}
          ></Coupons>
        </div>
      ) : (
        <LoadingDashboard
          isConnecting={props.isConnecting}
          connectWallet={props.connectWallet}
        ></LoadingDashboard>
      )}
    </div>
  );
}
